import {
  collection,
  getDocs,
  limit as fsLimit,
  query,
  Timestamp,
  doc,
  getDoc,
  where,
  orderBy,
  startAfter,
  DocumentSnapshot,
} from "firebase/firestore"

import { db } from "@/lib/firebase"
import { parsePersistedStudioWalkthroughVideoUrl } from "@/lib/studioWalkthroughUpload"
import type { PortfolioItem, Artist } from "@/types/models"

/** Valid booking subscription statuses */
export type BookingSubscriptionStatus = 'active' | 'inactive' | 'cancelled' | 'expired' | 'trial'

/**
 * Check if a specific artist has an active booking system
 * @param artistId The artist's ID
 * @returns true if the artist has an active or trial booking system subscription
 */
export async function checkArtistHasBookingSystem(artistId: string): Promise<boolean> {
  try {
    const bookingSystemRef = doc(db, 'bookingSystems', artistId)
    const bookingSystemDoc = await getDoc(bookingSystemRef)
    
    if (!bookingSystemDoc.exists()) {
      return false
    }
    
    const data = bookingSystemDoc.data()
    const bookingSubscription = data?.bookingSubscription
    
    if (!bookingSubscription) {
      return false
    }
    
    // Check if subscription is active or trial (both grant access)
    return bookingSubscription.status === 'active' || bookingSubscription.status === 'trial'
  } catch (error) {
    console.error(`[checkArtistHasBookingSystem] Error checking booking system for artist ${artistId}:`, error)
    return false
  }
}

/**
 * Get the booking subscription status for a specific artist
 * @param artistId The artist's ID
 * @returns The subscription status or null if no subscription exists
 */
export async function getArtistBookingSubscriptionStatus(artistId: string): Promise<BookingSubscriptionStatus | null> {
  try {
    const bookingSystemRef = doc(db, 'bookingSystems', artistId)
    const bookingSystemDoc = await getDoc(bookingSystemRef)
    
    if (!bookingSystemDoc.exists()) {
      return null
    }
    
    const data = bookingSystemDoc.data()
    const bookingSubscription = data?.bookingSubscription
    
    if (!bookingSubscription) {
      return null
    }
    
    return bookingSubscription.status as BookingSubscriptionStatus
  } catch (error) {
    console.error(`[getArtistBookingSubscriptionStatus] Error getting booking status for artist ${artistId}:`, error)
    return null
  }
}


export async function fetchRecentPortfolioItems(limitCount?: number) {
  try {
    // Create query without limit to fetch all items, or with limit if specified
    const q = limitCount 
      ? query(collection(db, "portfolioItems"), fsLimit(limitCount))
      : query(collection(db, "portfolioItems"))

    const snapshot = await getDocs(q)

    const items = snapshot.docs.map(doc => {
      const data = doc.data()

      return {
        id: doc.id,
        artistId: data.artistId as string,
        type: (data.type as PortfolioItem["type"]) ?? "image",
        storagePath: (data.storagePath as string) ?? "",
        imageUrl: data.imageUrl as string,
        compressedImageUrl: data.compressedImageUrl as string | undefined,
        width: (data.width as number) ?? 0,
        height: (data.height as number) ?? 0,
        styleTags: (data.styles as string[]) ?? [],
        caption: (data.caption as string) ?? "",
        createdAt: data.createdAt?.toDate?.() ?? new Date(),
        timedSaves: data.timedSaves ? data.timedSaves.map((saveData: any) => ({
          itemId: saveData.itemId,
          userId: saveData.userId,
          savedAt: saveData.savedAt?.toDate?.() || new Date(saveData.savedAt)
        })) : undefined,
      } satisfies PortfolioItem
    })

    return items
  } catch (error) {
    console.error('Error fetching portfolio items:', error)
    throw error
  }
}

export function getUniqueArtists(items: PortfolioItem[]) {
  const uniqueArtistIds = new Set(items.map(item => item.artistId))
  return Array.from(uniqueArtistIds)
}

export function getUniqueStyles(items: PortfolioItem[]) {
  const uniqueStyles = new Set<string>()
  items.forEach(item => {
    item.styleTags?.forEach(style => uniqueStyles.add(style))
  })
  return Array.from(uniqueStyles)
}

export async function fetchArtistById(artistId: string): Promise<Artist | null> {
  try {
    const artistDoc = await getDoc(doc(db, "artists", artistId))
    if (!artistDoc.exists()) return null

    const data = artistDoc.data()
    const artist = {
      id: artistDoc.id,
      userId: data.userId,
      firstName: data.firstName,
      lastName: data.lastName,
      displayName: data.displayName,
      bio: data.bio,
      location: data.location,
      styles: data.styles || [],
      avatarUrl: data.avatarUrl,
      backdropUrl: data.backdropUrl,
      studioWalkthroughVideoUrl: parsePersistedStudioWalkthroughVideoUrl(data.studioWalkthroughVideoUrl),
      createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(data.createdAt),
      isAvailable: data.isAvailable
    } satisfies Artist

    // No longer applying location filter - all artists are supported
    return artist
  } catch (error) {
    console.error("Error fetching artist:", error)
    return null
  }
}

export async function fetchArtistByDisplayName(displayName: string): Promise<Artist | null> {
  try {
    const artistsQuery = query(
      collection(db, "artists"),
      where("displayName", "==", displayName)
    )
    
    const snapshot = await getDocs(artistsQuery)
    
    if (snapshot.empty) return null
    
    const artistDoc = snapshot.docs[0]
    const data = artistDoc.data()
    
    const artist = {
      id: artistDoc.id,
      userId: data.userId,
      firstName: data.firstName,
      lastName: data.lastName,
      displayName: data.displayName,
      bio: data.bio,
      location: data.location,
      styles: data.styles || [],
      avatarUrl: data.avatarUrl,
      backdropUrl: data.backdropUrl,
      studioWalkthroughVideoUrl: parsePersistedStudioWalkthroughVideoUrl(data.studioWalkthroughVideoUrl),
      createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(data.createdAt),
      isAvailable: data.isAvailable
    } satisfies Artist
    
    return artist
  } catch (error) {
    console.error("Error fetching artist by display name:", error)
    return null
  }
}

export async function fetchArtistsByIds(artistIds: string[]): Promise<Artist[]> {
  const uniqueIds = Array.from(new Set(artistIds))
  const artists = await Promise.all(uniqueIds.map(id => fetchArtistById(id)))
  const filteredArtists = artists.filter((artist): artist is Artist => artist !== null)
  
  return filteredArtists
}

/**
 * Fetch a single flash by ID
 */
export async function fetchFlashById(flashId: string): Promise<PortfolioItem | null> {
  try {
    const flashDoc = await getDoc(doc(db, "flashes", flashId))
    if (!flashDoc.exists()) return null

    const data = flashDoc.data()
    return {
      id: flashDoc.id,
      artistId: data.artistId,
      type: data.type || "image",
      storagePath: data.storagePath || "",
      imageUrl: data.imageUrl,
      compressedImageUrl: data.compressedImageUrl,
      width: data.width || 0,
      height: data.height || 0,
      styleTags: data.styles || [],
      caption: data.caption || "",
      createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(data.createdAt),
      isFlash: true,
      minPrice: data.minPrice,
      minSize: data.minSize,
      available: data.available,
      timedSaves: data.timedSaves ? data.timedSaves.map((saveData: any) => ({
        itemId: saveData.itemId,
        userId: saveData.userId,
        savedAt: saveData.savedAt?.toDate?.() || new Date(saveData.savedAt)
      })) : undefined,
      discount: data.discount ? {
        absoluteAmount: data.discount.absoluteAmount,
        procentualAccount: data.discount.procentualAccount,
        newPrice: data.discount.newPrice,
        oldPrice: data.discount.oldPrice,
        discountStartsAt: data.discount.discountStartsAt?.toDate?.() || new Date(data.discount.discountStartsAt),
        discountEndsAt: data.discount.discountEndsAt?.toDate?.() || new Date(data.discount.discountEndsAt)
      } : undefined,
    } satisfies PortfolioItem
  } catch (error) {
    console.error("Error fetching flash:", error)
    return null
  }
}

// Removed fetchFlashItems - now handled by backend query_tattoos endpoint

export async function fetchFotwItems(fotwWeek: number) {
  try {
    const q = query(
      collection(db, "flashes"),
      where("fotw", "==", true),
      where("fotwWeek", "==", fotwWeek)
    )

    const snapshot = await getDocs(q)

    const items = snapshot.docs.map(doc => {
      const data = doc.data()

      return {
        id: doc.id,
        artistId: data.artistId as string,
        type: (data.type as PortfolioItem["type"]) ?? "image",
        storagePath: (data.storagePath as string) ?? "",
        imageUrl: data.imageUrl as string,
        compressedImageUrl: data.compressedImageUrl as string | undefined,
        width: (data.width as number) ?? 0,
        height: (data.height as number) ?? 0,
        caption: (data.caption as string) ?? "",
        createdAt: data.createdAt?.toDate?.() ?? new Date(),
        isFlash: true, // Mark as flash item
        minPrice: data.minPrice,
        minSize: data.minSize,
        fotw: data.fotw as boolean,
        fotwWeek: data.fotwWeek as number,
        available: data.available as boolean,
        votes: (data.votes as string[]) ?? [], // Legacy votes
        timedVotes: data.timedVotes ? data.timedVotes.map((voteData: any) => ({
          userId: voteData.userId,
          votedAt: voteData.votedAt?.toDate?.() || new Date(voteData.votedAt)
        })) : undefined,
        timedSaves: data.timedSaves ? data.timedSaves.map((saveData: any) => ({
          itemId: saveData.itemId,
          userId: saveData.userId,
          savedAt: saveData.savedAt?.toDate?.() || new Date(saveData.savedAt)
        })) : undefined,
        discount: data.discount ? {
          absoluteAmount: data.discount.absoluteAmount,
          procentualAccount: data.discount.procentualAccount,
          newPrice: data.discount.newPrice,
          oldPrice: data.discount.oldPrice,
          discountStartsAt: data.discount.discountStartsAt?.toDate?.() || new Date(data.discount.discountStartsAt),
          discountEndsAt: data.discount.discountEndsAt?.toDate?.() || new Date(data.discount.discountEndsAt)
        } : undefined,
      } satisfies PortfolioItem
    })

    // Sort by timedVotes count (descending) for leaderboard, fallback to votes for legacy
    items.sort((a, b) => {
      const aVotes = (a.timedVotes?.length || a.votes?.length || 0)
      const bVotes = (b.timedVotes?.length || b.votes?.length || 0)
      return bVotes - aVotes
    })

    return items
  } catch (error) {
    console.error('Error fetching FOTW items:', error)
    throw error
  }
}

// New function to get initial balanced feed (up to 20 items per artist)
export async function fetchBalancedFlashFeed(maxItemsPerArtist = 20) {
  try {
    
    // First, get all flash items
    const allQuery = query(collection(db, "flashes"), where("available", "==", true))
    const snapshot = await getDocs(allQuery)
    
    // Group by artist
    const itemsByArtist = new Map<string, PortfolioItem[]>()
    
    snapshot.docs.forEach(doc => {
      const data = doc.data()
      if (data.fotw === true) return // Skip fotw items
      
      const item: PortfolioItem = {
        id: doc.id,
        artistId: data.artistId as string,
        type: (data.type as PortfolioItem["type"]) ?? "image",
        storagePath: (data.storagePath as string) ?? "",
        imageUrl: data.imageUrl as string,
        compressedImageUrl: data.compressedImageUrl as string | undefined,
        width: (data.width as number) ?? 0,
        height: (data.height as number) ?? 0,
        styleTags: (data.styles as string[]) ?? [], // Map "styles" field to "styleTags" for consistency
        caption: (data.caption as string) ?? "",
        createdAt: data.createdAt?.toDate?.() ?? new Date(),
        isFlash: true,
        minPrice: data.minPrice as string,
        minSize: data.minSize as string,
        available: data.available as boolean,
        accessCode: data.accessCode as string | undefined,
      }
      
      if (!itemsByArtist.has(item.artistId)) {
        itemsByArtist.set(item.artistId, [])
      }
      itemsByArtist.get(item.artistId)!.push(item)
    })
    
    // Take up to maxItemsPerArtist from each artist
    const balancedItems: PortfolioItem[] = []
    
    for (const [, artistItems] of itemsByArtist) {
      // Shuffle and take up to maxItemsPerArtist
      const shuffled = [...artistItems].sort(() => Math.random() - 0.5)
      const selected = shuffled.slice(0, maxItemsPerArtist)
      balancedItems.push(...selected)
      
    }
    
    // Shuffle the final result
    const finalItems = balancedItems.sort(() => Math.random() - 0.5)
    
    return finalItems
  } catch (error) {
    console.error("[fetchBalancedFlashFeed] Error fetching balanced feed:", error)
    throw error
  }
}

/**
 * Weaves tattoos from different artists together to avoid chunks of the same artist.
 * Uses an improved distribution strategy that accounts for column-based layouts.
 * Distributes items across multiple columns simultaneously to prevent artist clustering.
 * 
 * NOTE: This function is kept for EventFlashGrid which has special handling requirements.
 * For regular flash/tattoo grids, sorting is handled by the backend.
 */
export function weaveTattoosByArtist(items: PortfolioItem[], columns = 2): PortfolioItem[] {
  if (items.length === 0) return items

  // Group tattoos by artist
  const artistGroups = new Map<string, PortfolioItem[]>()
  
  items.forEach(item => {
    if (!artistGroups.has(item.artistId)) {
      artistGroups.set(item.artistId, [])
    }
    artistGroups.get(item.artistId)!.push(item)
  })

  // Convert to array of [artistId, items] pairs
  const artistGroupsArray = Array.from(artistGroups.entries())
  
  // Find the minimum group size to determine equal distribution rounds
  const groupSizes = artistGroupsArray.map(([, group]) => group.length)
  const minGroupSize = Math.min(...groupSizes)

  const result: PortfolioItem[] = []

  // Phase 1: Round-robin distribution with shuffled artist order each round
  for (let round = 0; round < minGroupSize; round++) {
    // Shuffle the artist order for this round to avoid predictable patterns
    const shuffledArtists = [...artistGroupsArray].sort(() => Math.random() - 0.5)
    
    for (const [, group] of shuffledArtists) {
      if (group.length > round) {
        result.push(group[round])
      }
    }
  }

  // Phase 2: Column-aware distribution of remaining items
  // Group remaining items by artist to maintain control over distribution
  const remainingByArtist = new Map<string, PortfolioItem[]>()
  
  for (const [artistId, group] of artistGroupsArray) {
    if (group.length > minGroupSize) {
      remainingByArtist.set(artistId, group.slice(minGroupSize))
    }
  }

  // If we have remaining items, distribute them column-aware
  if (remainingByArtist.size > 0) {
    const baseLength = result.length
    const remainingArtists = Array.from(remainingByArtist.keys())
    
    // Create a distribution pattern that fills multiple columns simultaneously
    const distributionPattern: PortfolioItem[] = []
    
    // Continue round-robin with remaining items, but in column-aware chunks
    const maxRemainingItems = Math.max(...Array.from(remainingByArtist.values()).map(items => items.length))
    
    for (let round = 0; round < maxRemainingItems; round++) {
      // Shuffle artist order for each round to avoid patterns
      const shuffledRemainingArtists = [...remainingArtists].sort(() => Math.random() - 0.5)
      
      // For each artist in this round, add up to 'columns' items to fill multiple columns
      for (const artistId of shuffledRemainingArtists) {
        const artistItems = remainingByArtist.get(artistId)!
        
        // Add up to 'columns' items from this artist in this round
        for (let col = 0; col < columns && (round * columns + col) < artistItems.length; col++) {
          const itemIndex = round * columns + col
          if (itemIndex < artistItems.length) {
            distributionPattern.push(artistItems[itemIndex])
          }
        }
      }
    }

    // Now integrate the distribution pattern with the base result
    // Instead of inserting at regular intervals, weave them throughout
    const totalItems = baseLength + distributionPattern.length
    const integrationRatio = baseLength / totalItems
    
    let baseIndex = 0
    let patternIndex = 0
    const finalResult: PortfolioItem[] = []
    
    while (baseIndex < baseLength && patternIndex < distributionPattern.length) {
      // Calculate how many base items to add before next pattern item
      const baseItemsToAdd = Math.max(1, Math.round(integrationRatio * (columns * 2)))
      
      // Add base items
      for (let i = 0; i < baseItemsToAdd && baseIndex < baseLength; i++) {
        finalResult.push(result[baseIndex++])
      }
      
      // Add pattern items (up to columns items to fill multiple columns)
      for (let i = 0; i < columns && patternIndex < distributionPattern.length; i++) {
        finalResult.push(distributionPattern[patternIndex++])
      }
    }
    
    // Add any remaining base items
    while (baseIndex < baseLength) {
      finalResult.push(result[baseIndex++])
    }
    
    // Add any remaining pattern items
    while (patternIndex < distributionPattern.length) {
      finalResult.push(distributionPattern[patternIndex++])
    }
    
    return finalResult
  }

  return result
}

// Removed fetchPortfolioItems - now handled by backend query_tattoos endpoint

export async function fetchEventFlashes(
  eventId: string,
  limit = 30,
  lastDoc?: DocumentSnapshot
) {
  try {
    console.log("[fetchEventFlashes] Starting fetch for event:", eventId, { limit, hasLastDoc: !!lastDoc })
    
    let baseQuery = query(
      collection(db, "flashes"), 
      where("eventId", "==", eventId),
      where("available", "==", true),
      orderBy("__name__") // Order by document ID for consistent pagination
    )
    
    // Add pagination cursor if provided
    if (lastDoc) {
      baseQuery = query(baseQuery, startAfter(lastDoc))
      console.log("[fetchEventFlashes] Added pagination cursor")
    }
    
    // Add limit to the query
    baseQuery = query(baseQuery, fsLimit(limit))

    const snapshot = await getDocs(baseQuery)

    const items = snapshot.docs.map(doc => {
      const data = doc.data()
      return {
        id: doc.id,
        artistId: data.artistId as string,
        type: (data.type as PortfolioItem["type"]) ?? "image",
        storagePath: (data.storagePath as string) ?? "",
        imageUrl: data.imageUrl as string,
        compressedImageUrl: data.compressedImageUrl as string | undefined,
        width: (data.width as number) ?? 0,
        height: (data.height as number) ?? 0,
        styleTags: (data.styles as string[]) ?? [],
        caption: (data.caption as string) ?? "",
        createdAt: data.createdAt?.toDate?.() ?? new Date(),
        isFlash: true,
        minPrice: data.minPrice as string,
        minSize: data.minSize as string,
        timedSaves: data.timedSaves ? data.timedSaves.map((saveData: any) => ({
          itemId: saveData.itemId,
          userId: saveData.userId,
          savedAt: saveData.savedAt?.toDate?.() || new Date(saveData.savedAt)
        })) : undefined,
        available: data.available as boolean,
        accessCode: data.accessCode as string | undefined,
        eventId: data.eventId as string,
        discount: data.discount ? {
          absoluteAmount: data.discount.absoluteAmount,
          procentualAccount: data.discount.procentualAccount,
          newPrice: data.discount.newPrice,
          oldPrice: data.discount.oldPrice,
          discountStartsAt: data.discount.discountStartsAt?.toDate?.() || new Date(data.discount.discountStartsAt),
          discountEndsAt: data.discount.discountEndsAt?.toDate?.() || new Date(data.discount.discountEndsAt)
        } : undefined,
      } satisfies PortfolioItem
    })

    console.log("[fetchEventFlashes] Processed items:", items.length)
    
    // Return items and last document for pagination
    const lastDocument = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null
    
    return {
      items,
      lastDoc: lastDocument,
      hasMore: snapshot.docs.length === limit // If we got a full batch, there might be more
    }
  } catch (error) {
    console.error("[fetchEventFlashes] Error fetching event flash items:", error)
    throw error
  }
}